import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, Filter, MoreVertical, Eye, Edit, Pause, Play, Archive, UserCog,
  ExternalLink, X, ChevronRight, Mail, Calendar, Activity
} from 'lucide-react';
import LiveURLsPanel from './LiveURLsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaaSTenants() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    template_slug: '',
    subscription_status: '',
    search: ''
  });
  const [showDetail, setShowDetail] = useState(false);
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, [filters]);

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetail(tenantId);
    }
  }, [tenantId]);

  const fetchTenants = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.template_slug) params.append('template_slug', filters.template_slug);
      if (filters.subscription_status) params.append('subscription_status', filters.subscription_status);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/saas/tenants?${params.toString()}`);
      setTenants(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetail = async (id) => {
    try {
      const response = await axios.get(`${API}/saas/tenants/${id}`);
      setSelectedTenant(response.data);
      setShowDetail(true);
    } catch (error) {
      console.error('Error fetching tenant detail:', error);
      toast.error('Failed to load tenant details');
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    try {
      await axios.put(`${API}/saas/tenants/${tenantId}/status?status=${newStatus}`);
      toast.success(`Tenant ${newStatus}`);
      fetchTenants();
      if (selectedTenant?.id === tenantId) {
        fetchTenantDetail(tenantId);
      }
    } catch (error) {
      toast.error('Failed to update tenant status');
    }
  };

  const handleImpersonate = async (tenant) => {
    try {
      const response = await axios.post(`${API}/saas/impersonate/${tenant.id}`);
      const sessionData = response.data;
      setImpersonating(sessionData);
      // Store impersonation session in localStorage for the banner to pick up
      localStorage.setItem('impersonation_session', JSON.stringify(sessionData));
      localStorage.setItem('tenant_id', tenant.id);
      toast.success(`Now impersonating ${tenant.name}`);
      // Navigate to tenant dashboard in new tab
      window.open('/dashboard', '_blank');
    } catch (error) {
      toast.error('Failed to start impersonation');
    }
  };

  const endImpersonation = async () => {
    if (!impersonating) return;
    try {
      await axios.post(`${API}/saas/impersonate/${impersonating.session_id}/end`);
      setImpersonating(null);
      localStorage.removeItem('impersonation_session');
      localStorage.removeItem('tenant_id');
      toast.success('Impersonation ended');
    } catch (error) {
      toast.error('Failed to end impersonation');
    }
  };

  return (
    <div className="saas-tenants" data-testid="saas-tenants">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="impersonation-banner">
          <UserCog size={18} />
          <span>Impersonating: <strong>{impersonating.tenant_name}</strong></span>
          <button onClick={() => window.open('/dashboard', '_blank')}>
            Open Dashboard <ExternalLink size={14} />
          </button>
          <button className="end-btn" onClick={endImpersonation}>
            End Impersonation
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="saas-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search tenants..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
        
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        
        <select
          value={filters.subscription_status}
          onChange={(e) => setFilters(prev => ({ ...prev, subscription_status: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Subscriptions</option>
          <option value="trialing">Trialing</option>
          <option value="active">Active</option>
          <option value="past_due">Past Due</option>
          <option value="canceled">Canceled</option>
        </select>

        <span className="filter-count">{total} tenants</span>
      </div>

      {/* Tenants Table */}
      <div className="saas-table-container">
        <table className="saas-table" data-testid="tenants-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Template</th>
              <th>Status</th>
              <th>Subscription</th>
              <th>Users</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(tenant => (
              <tr key={tenant.id}>
                <td>
                  <div className="tenant-cell">
                    <div className="tenant-avatar">{tenant.name.charAt(0)}</div>
                    <div className="tenant-info">
                      <strong>{tenant.name}</strong>
                      <span>{tenant.slug}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="template-badge">{tenant.template_slug}</span>
                </td>
                <td>
                  <span className={`status-badge ${tenant.status}`}>{tenant.status}</span>
                </td>
                <td>
                  <span className={`status-badge ${tenant.subscription_status || 'trialing'}`}>
                    {tenant.subscription_status || 'trialing'}
                  </span>
                </td>
                <td>{tenant.users_count || 1}</td>
                <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn"
                      onClick={() => fetchTenantDetail(tenant.id)}
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleImpersonate(tenant)}
                      title="Impersonate"
                    >
                      <UserCog size={16} />
                    </button>
                    <div className="action-dropdown">
                      <button className="action-btn">
                        <MoreVertical size={16} />
                      </button>
                      <div className="dropdown-menu">
                        <button onClick={() => handleStatusChange(tenant.id, tenant.status === 'suspended' ? 'active' : 'suspended')}>
                          {tenant.status === 'suspended' ? <Play size={14} /> : <Pause size={14} />}
                          {tenant.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                        </button>
                        <button onClick={() => handleStatusChange(tenant.id, 'archived')}>
                          <Archive size={14} />
                          Archive
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tenants.length === 0 && !loading && (
          <div className="empty-state">
            <p>No tenants found</p>
          </div>
        )}
      </div>

      {/* Tenant Detail Slide-over */}
      {showDetail && selectedTenant && (
        <div className="slide-over-overlay" onClick={() => setShowDetail(false)}>
          <div className="slide-over" onClick={e => e.stopPropagation()} data-testid="tenant-detail">
            <div className="slide-over-header">
              <h3>{selectedTenant.name}</h3>
              <button className="close-btn" onClick={() => setShowDetail(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="slide-over-content">
              {/* Basic Info */}
              <section className="detail-section">
                <h4>Basic Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Subdomain</label>
                    <span>{selectedTenant.slug}</span>
                  </div>
                  <div className="detail-item">
                    <label>Owner Email</label>
                    <span>{selectedTenant.owner_email || 'Not set'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${selectedTenant.status}`}>{selectedTenant.status}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created</label>
                    <span>{new Date(selectedTenant.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </section>

              {/* 🌐 Live URLs — public homepage, enrollment, tour, parent portal, login, CRM */}
              <LiveURLsPanel tenant={selectedTenant} />

              {/* Provisioning Info */}
              <section className="detail-section">
                <h4>Provisioning</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Template</label>
                    <span>{selectedTenant.template_info?.name || selectedTenant.template_slug}</span>
                  </div>
                  <div className="detail-item">
                    <label>Modules Enabled</label>
                    <span>{selectedTenant.modules_enabled?.length || 0}</span>
                  </div>
                  <div className="detail-item">
                    <label>Provisioning Status</label>
                    <span className={`status-badge ${selectedTenant.provisioning_status}`}>
                      {selectedTenant.provisioning_status}
                    </span>
                  </div>
                </div>
              </section>

              {/* Subscription */}
              <section className="detail-section">
                <h4>Subscription</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Plan</label>
                    <span>{selectedTenant.subscription?.plan_name || selectedTenant.subscription_plan || 'starter'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${selectedTenant.subscription_status || 'trialing'}`}>
                      {selectedTenant.subscription_status || 'trialing'}
                    </span>
                  </div>
                  {selectedTenant.trial_ends_at && (
                    <div className="detail-item">
                      <label>Trial Ends</label>
                      <span>{new Date(selectedTenant.trial_ends_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Usage Summary */}
              {selectedTenant.usage && (
                <section className="detail-section">
                  <h4>Usage Summary</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Users</label>
                      <span>{selectedTenant.usage.users_count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <label>CRM Records</label>
                      <span>{selectedTenant.usage.crm_records_count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <label>Appointments</label>
                      <span>{selectedTenant.usage.appointments_count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <label>Automation Runs</label>
                      <span>{selectedTenant.usage.automation_runs_count || 0}</span>
                    </div>
                  </div>
                </section>
              )}

              {/* SaaS Ops */}
              <section className="detail-section">
                <h4>SaaS Operations</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Open Errors</label>
                    <span className={selectedTenant.error_count > 0 ? 'error-count' : ''}>
                      {selectedTenant.error_count || 0}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Open Tickets</label>
                    <span>{selectedTenant.ticket_count || 0}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Activity</label>
                    <span>
                      {selectedTenant.last_activity_at 
                        ? new Date(selectedTenant.last_activity_at).toLocaleString()
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Recent Logs */}
              {selectedTenant.recent_logs?.length > 0 && (
                <section className="detail-section">
                  <h4>Recent Activity</h4>
                  <div className="recent-logs">
                    {selectedTenant.recent_logs.slice(0, 5).map((log, idx) => (
                      <div key={idx} className="log-item">
                        <span className="log-action">{log.action}</span>
                        <span className="log-time">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Actions */}
              <div className="slide-over-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => handleImpersonate(selectedTenant)}
                >
                  <UserCog size={16} />
                  Impersonate
                </button>
                <button 
                  className={`btn-${selectedTenant.status === 'suspended' ? 'primary' : 'warning'}`}
                  onClick={() => handleStatusChange(selectedTenant.id, selectedTenant.status === 'suspended' ? 'active' : 'suspended')}
                >
                  {selectedTenant.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
